import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { ApiError, api, uploadDocument } from '../api/client.js'
import type { ObjectDetail } from '../api/types.js'
import { Icon } from '../components/icons.js'
import { useToast } from '../components/toast.js'
import { EmptyState, Notice, Spinner } from '../components/ui.js'
import { formatDateTime, formatFileSize } from '../lib/format.js'

export interface DocumentRow {
  id: string
  objectId: string
  name: string
  mime: string
  bytes: number
  shareWithCustomer?: boolean
  uploadedByName?: string | null
  createdAt: string
  path: string[]
}

/**
 * Dokumente am Objekt: Schlüsselplan, Wartungsvertrag, Leistungsverzeichnis.
 * Filed where they belong, listed from every level above, and shared with the
 * customer one by one, never by default.
 */
export function DocumentsTab({ node }: { node: ObjectDetail }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [share, setShare] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const key = ['documents', node.id]
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => api<DocumentRow[]>(`/api/objects/${node.id}/documents`) })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['documents'] })

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) await uploadDocument(node.id, file, share)
      return files.length
    },
    onSuccess: async (count) => {
      setError(null)
      toast(count === 1 ? 'Dokument abgelegt.' : `${count} Dokumente abgelegt.`)
      await refresh()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Hochladen fehlgeschlagen.'),
  })
  const toggleShare = useMutation({
    mutationFn: (doc: DocumentRow) => api(`/api/documents/${doc.id}`, { method: 'PATCH', body: { shareWithCustomer: !doc.shareWithCustomer } }),
    onSuccess: async (_r, doc) => {
      toast(doc.shareWithCustomer ? `„${doc.name}“ ist nur noch intern.` : `„${doc.name}“ ist im Kundenportal sichtbar.`)
      await refresh()
    },
  })
  const remove = useMutation({
    mutationFn: (doc: DocumentRow) => api(`/api/documents/${doc.id}`, { method: 'DELETE' }),
    onSuccess: async (_r, doc) => {
      toast(`„${doc.name}“ gelöscht.`)
      await refresh()
    },
  })

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Dokumente</h2>
          <div className="muted small">
            PDF, Bilder, Word, Excel. Dokumente von Objekten darunter erscheinen hier mit. Für den Kunden sichtbar nur, was freigegeben ist.
          </div>
        </div>
        <div className="doc-actions">
          <label className="check-inline small">
            <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} /> für den Kunden freigeben
          </label>
          <button type="button" className="btn" disabled={upload.isPending} onClick={() => input.current?.click()}>
            <Icon name="upload" /> {upload.isPending ? 'Wird hochgeladen …' : 'Hochladen'}
          </button>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              e.target.value = ''
              if (files.length > 0) upload.mutate(files)
            }}
          />
        </div>
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {isLoading ? (
        <Spinner />
      ) : !data || data.length === 0 ? (
        <EmptyState title="Noch keine Dokumente" hint="z. B. Schlüsselplan, Wartungsvertrag oder Leistungsverzeichnis hier ablegen." />
      ) : (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr>
                <th>Dokument</th>
                <th>Wo</th>
                <th className="nowrap">Abgelegt</th>
                <th className="nowrap">Kunde</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <a href={`/api/documents/${doc.id}`} target="_blank" rel="noreferrer" className="strong">
                      <Icon name="file" size={14} /> {doc.name}
                    </a>
                    <div className="muted small">{formatFileSize(doc.bytes)}</div>
                  </td>
                  <td className="small">{doc.objectId === node.id ? <span className="muted">hier</span> : doc.path.join(' › ')}</td>
                  <td className="nowrap small">
                    {formatDateTime(doc.createdAt)}
                    {doc.uploadedByName && <div className="muted">{doc.uploadedByName}</div>}
                  </td>
                  <td className="nowrap">
                    <button
                      type="button"
                      className={`badge ${doc.shareWithCustomer ? 'done' : ''}`}
                      style={{ border: 0, cursor: 'pointer' }}
                      title="Klicken zum Umschalten"
                      onClick={() => toggleShare.mutate(doc)}
                    >
                      {doc.shareWithCustomer ? 'freigegeben' : 'intern'}
                    </button>
                  </td>
                  <td className="nowrap" style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn ghost small"
                      onClick={() => {
                        if (confirm(`„${doc.name}“ wirklich löschen?`)) remove.mutate(doc)
                      }}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
